import { PurchaseOrderCardArgsType } from "./type";
import React from "react";
import { useDownloadPurchaseOrderInvoice, useSendPurchaseOrderSummary } from "../../dataloader";
import { ApiPurchaseOrderSummaryRequest } from "../../../../../../shared/types/ApiPurchaseOrderSummaryRequest";
import { useAuth } from "../../../../../../shared/context/auth";
import { InvoicePaymentRequestStatus } from "shared/enums/InvoicePaymentRequestStatus";
import { downloadBlobFile } from "shared/utils";
import PdfTemplateName from "shared/enums/PdfTemplateName";
import { useEnabledFeatures } from "../../../../context/enabledFeatures";
import { useMyExpAnalytics } from "apps/myExperience/context/analyticsWrapper";
import {
  AnalyticsActionNameEnum,
  AnalyticsContextEntityTypeEnum,
  AnalyticsEventNameEnum,
  AnalyticsScreenNameEnum
} from "shared/enums/AnalyticsEnums";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

export default function usePurchaseOrderCardService(props: PurchaseOrderCardArgsType) {
  const auth = useAuth();
  const { logEvent } = useMyExpAnalytics();
  const { isPDFTemplateEnabled } = useEnabledFeatures();
  const { mutateAsync: sendPurchaseOrderSummaryEmail } = useSendPurchaseOrderSummary();
  const { mutateAsync: downloadPurchaseOrderInvoice } = useDownloadPurchaseOrderInvoice();

  const purchaseOrderId = React.useMemo(() => {
    return props.purchaseOrder.identifier;
  }, [props.purchaseOrder]);

  const isTemplateEnabledForDownload = React.useMemo(() => {
    return isPDFTemplateEnabled(PdfTemplateName.PURCHASE_ORDER_RECEIPT);
  }, [isPDFTemplateEnabled]);

  const sendInvoiceHandler = React.useCallback(async () => {
    const reqObj: ApiPurchaseOrderSummaryRequest = {
      registrationParticipantId: auth.userDetail?.id as string,
      purchaseOrders: [props.purchaseOrder.purchaseOrderId]
    };
    try {
      logEvent(
        AnalyticsEventNameEnum.CLICK,
        {
          screenType: AnalyticsScreenNameEnum.PURCHASE_INVOICE,
          action: AnalyticsActionNameEnum.EMAIL,
          contextEntityType: AnalyticsContextEntityTypeEnum.PURCHASE_ORDER,
          contextEntityId: props.purchaseOrder.purchaseOrderId
        },
        AnalyticsDatastoreType.MIXPANEL_INTERNAL
      );
      await sendPurchaseOrderSummaryEmail(reqObj);
    } catch (e) {}
  }, [auth.userDetail?.id, props.purchaseOrder.purchaseOrderId, sendPurchaseOrderSummaryEmail, logEvent]);

  const downloadInvoiceHandler = React.useCallback(async () => {
    logEvent(
      AnalyticsEventNameEnum.CLICK,
      {
        screenType: AnalyticsScreenNameEnum.PURCHASE_INVOICE,
        action: AnalyticsActionNameEnum.DOWNLOAD,
        contextEntityType: AnalyticsContextEntityTypeEnum.PURCHASE_ORDER,
        contextEntityId: props.purchaseOrder.purchaseOrderId
      },
      AnalyticsDatastoreType.MIXPANEL_INTERNAL
    );
    downloadBlobFile({
      downloadFn: downloadPurchaseOrderInvoice,
      params: { id: props.purchaseOrder.purchaseOrderId }
    });
  }, [downloadPurchaseOrderInvoice, props.purchaseOrder.purchaseOrderId, logEvent]);

  const isInvoicePaymentPending = React.useMemo(
    () =>
      props.purchaseOrder.forwardedToInvoicePayer &&
      props.purchaseOrder.invoicePaymentRequest?.status === InvoicePaymentRequestStatus.PENDING,
    [props.purchaseOrder.forwardedToInvoicePayer, props.purchaseOrder.invoicePaymentRequest?.status]
  );

  return {
    purchaseOrderId,
    sendInvoiceHandler,
    isInvoicePaymentPending,
    isTemplateEnabledForDownload,
    downloadInvoiceHandler
  };
}
